import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '~/hooks/useTheme';
import { apiService } from '~/services/apiService';
import { useAuth } from '~/hooks/useAuth';
import { UserIcon } from '~/components/Icons/Icons';
import { toast } from 'react-toastify';
import Logo from '~/components/Logo/Logo';
import classNames from 'classnames';
import styles from './Header.module.css';

function Header() {
  const { login, joinRoom } = useAuth();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');

  const initialFormState = {
    nick: '',
    email: '',
    password: '',
    gender: ''
  };

  const [formData, setFormData] = useState(initialFormState);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const { theme, toggleTheme } = useTheme();
  const menuRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (location.pathname.startsWith('/aktywacja')) return;

    const activationComplete = sessionStorage.getItem('activationComplete');
    if (activationComplete !== null) {
      sessionStorage.removeItem('activationComplete');
      try {
        const { emailOrNick, password } = JSON.parse(activationComplete);
        if (emailOrNick && password) {
          setFormData(prev => ({ ...prev, email: emailOrNick, password }));
        }
      } catch {
        // ignore
      }
      setAuthMode('login');
      setIsAuthOpen(true);
      return;
    }

    const pending = sessionStorage.getItem('pendingActivationLogin');
    if (pending) {
      try {
        const { emailOrNick, password } = JSON.parse(pending);
        sessionStorage.removeItem('pendingActivationLogin');
        setFormData(prev => ({ ...prev, email: emailOrNick, password }));
        setAuthMode('login');
        setIsAuthOpen(true);
      } catch {
        sessionStorage.removeItem('pendingActivationLogin');
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isMenuOpen && menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
      if (isAuthOpen && modalRef.current && !modalRef.current.contains(e.target as Node)) {
        setIsAuthOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen, isAuthOpen]);

  const closeMenu = () => setIsMenuOpen(false);

  const openAuth = (mode: 'login' | 'register') => {
    setAuthMode(mode);
    setErrors([]);
    setFormData(initialFormState);
    setRememberMe(false);
    setShowPassword(false);
    setIsAuthOpen(true);
    closeMenu();
  };

  const handleTabChange = (mode: 'login' | 'register') => {
    setAuthMode(mode);
    setErrors([]);
    setFormData(initialFormState);
    setRememberMe(false);
    setShowPassword(false);
  };

  const validate = () => {
    const newErrors: string[] = [];

    if (!formData.email) {
      newErrors.push('email');
    } else if (authMode === 'register') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) newErrors.push('email');
    }

    if (authMode === 'register') {
      if (!formData.nick || formData.nick.length > 30) newErrors.push('nick');
      if (!formData.gender) newErrors.push('gender');
    }

    if (!formData.password) {
      newErrors.push('password');
    } else if (authMode === 'register') {
      if (formData.password.length < 4) {
        newErrors.push('password');
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleForgotPassword = async () => {
    // Reset wysyłamy na adres powiązany z kontem, które próbowano zalogować.
    // Pole "email" w trybie logowania przyjmuje e-mail LUB nick — backend rozpozna jedno i drugie.
    const name = formData.email.trim();
    if (!name) {
      setErrors(['email']);
      toast.warning('Podaj e-mail lub nick, na który chcesz odzyskać hasło.', { position: 'top-center' });
      return;
    }

    if (isSendingReset) return;
    setIsSendingReset(true);

    try {
      const response = await apiService('/auth/forgot-password', 'POST', { name });
      toast.success(
        response?.message || 'Jeśli konto istnieje, wysłaliśmy link do zresetowania hasła na powiązany adres e-mail.',
        { position: 'top-center', autoClose: 8000 }
      );
    } catch (error: any) {
      toast.error('Wystąpił problem z połączeniem z serwerem. Spróbuj ponownie później.', { position: 'top-center' });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      if (authMode === 'register') {
        const registerData = {
          nick: formData.nick,
          email: formData.email,
          password: formData.password,
          gender: formData.gender
        };

        const response = await apiService('/auth/register', 'POST', registerData);

        if (response.status === 201) {
          toast.success(response.message, { position: "top-center", autoClose: false });
          sessionStorage.setItem('pendingActivationLogin', JSON.stringify({
            emailOrNick: registerData.nick || registerData.email,
            password: registerData.password
          }));
          setAuthMode('login');
          setFormData({
            ...initialFormState,
            email: registerData.nick || registerData.email,
            password: registerData.password
          });
          return;
        }

        if (response.status === 409) {
          if (response.field) {
            setErrors([response.field]);
          } else {
            setErrors(['nick', 'email']);
          }
          toast.warning(response.message, { position: "top-center" });
          return;
        }

      } else {
        // --- LOGOWANIE ---
        try {
          const user = await login(formData.email, formData.password, rememberMe);
          setIsAuthOpen(false);
          toast.success(`Witaj, ${user.nick}! Dołączasz do czatu...`, { position: 'top-center', autoClose: 3000 });
          const joined = await joinRoom(user);
          if (!joined) {
            toast.warning('Twój nick jest już aktywny w czacie. Odśwież stronę.', { position: 'top-center' });
          }
        } catch (loginError: any) {
          const msg = loginError.message?.toLowerCase() || '';
          if (msg.includes('zablokowane')) {
            toast.error(loginError.message, { position: "top-center" });
          } else {
            setErrors(['email', 'password']);
            toast.error(loginError.message || 'Niepoprawny login lub hasło.', { position: "top-center" });
          }
        }
      }
    } catch (error: any) {
      console.error('Krytyczny błąd połączenia lub serwera:', error);
      toast.error('Wystąpił problem z połączeniem z serwerem. Spróbuj ponownie później.', {
        position: "top-center"
      });
    }
  };

  const getLinkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? `${styles.menuLink} ${styles.activeLink}` : styles.menuLink;

  return (
    <>
      <header className={styles.mainHeader}>
        <div className={styles.headerContent}>
          <Logo />
          <div className={styles.headerTools}>
            <button className={styles.iconBtn} onClick={() => openAuth('login')}>
              <UserIcon className={styles.userIcon} />
            </button>
            <button className={styles.iconBtn} onClick={toggleTheme}>
              {theme === 'dark' ? '🌙' : '☀️'}
            </button>
            <button
              className={`${styles.hamburgerBtn} ${isMenuOpen ? styles.hamburgerActive : ''}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span></span><span></span><span></span>
            </button>
          </div>
        </div>
      </header>

      <div ref={menuRef} className={`${styles.sideMenu} ${isMenuOpen ? styles.sideMenuOpen : ''}`}>
        <div className={styles.menuHeader}>
          <div className={styles.logoText}>MENU</div>
          <button className={styles.closeBtn} onClick={closeMenu}>✕</button>
        </div>
        <nav className={styles.menuNav}>
          <NavLink to="/" onClick={closeMenu} className={getLinkClass} end>
            <span>🏠</span> Strona Główna
          </NavLink>
          <button onClick={() => openAuth('login')} className={styles.menuLinkBtn}>
            <span>👤</span> Zaloguj się
          </button>
          <NavLink to="/o-nas" onClick={closeMenu} className={getLinkClass}>
            <span>👥</span> O nas
          </NavLink>
          <NavLink to="/kontakt" onClick={closeMenu} className={getLinkClass}>
            <span>📞</span> Kontakt
          </NavLink>
          <NavLink to="/polityka-prywatnosci" onClick={closeMenu} className={getLinkClass}>
            <span>🔒</span> Polityka prywatności
          </NavLink>
          <NavLink to="/regulamin" onClick={closeMenu} className={getLinkClass}>
            <span>📜</span> Regulamin
          </NavLink>
          <NavLink to="/pomoc" onClick={closeMenu} className={getLinkClass}>
            <span>💡</span> Pomoc i FAQ
          </NavLink>
        </nav>
      </div>

      {isAuthOpen && (
        <div className={styles.modalOverlay}>
          <div ref={modalRef} className={styles.authModal}>
            <button className={styles.modalCloseBtn} onClick={() => setIsAuthOpen(false)}>✕</button>

            <div className={styles.authTabs}>
              <button
                className={`${styles.authTab} ${authMode === 'login' ? styles.authTabActive : ''}`}
                onClick={() => handleTabChange('login')}
              >
                Logowanie
              </button>
              <button
                className={`${styles.authTab} ${authMode === 'register' ? styles.authTabActive : ''}`}
                onClick={() => handleTabChange('register')}
              >
                Rejestracja
              </button>
            </div>

            <form className={styles.authForm} onSubmit={handleSubmit} noValidate>
              {authMode === 'register' && (
                <div className={styles.inputGroup}>
                  <div className={styles.labelWithCounter}>
                    <label className={errors.includes('nick') ? styles.labelError : ''}>Nick</label>
                    <span className={classNames(styles.charCounter, { [styles.counterError]: formData.nick.length >= 30 })}>
                      <span className={styles.charCounterInfo}>{formData.nick.length}/30</span>
                    </span>
                  </div>
                  <input
                    type="text"
                    maxLength={30}
                    className={errors.includes('nick') ? styles.inputError : ''}
                    value={formData.nick}
                    onChange={(e) => setFormData({ ...formData, nick: e.target.value.slice(0, 30) })}
                    placeholder="Twój nick..."
                  />
                </div>
              )}

              <div className={styles.inputGroup}>
                <label className={errors.includes('email') ? styles.labelError : ''}>
                  E-mail {authMode === 'login' && 'lub Nick'}
                </label>
                <input
                  type="email"
                  className={errors.includes('email') ? styles.inputError : ''}
                  value={formData.email}
                  onChange={(e) => {
                    // BLOKADA SPACJI w polu email
                    const noSpaces = e.target.value.replace(/\s/g, '');
                    setFormData({ ...formData, email: noSpaces });
                  }}
                  placeholder="E-mail lub nick"
                />
              </div>

              <div className={styles.inputGroup}>
                <label className={errors.includes('password') ? styles.labelError : ''}>
                  Hasło {authMode === 'register' && '(min. 4 znaki)'}
                </label>
                <div className={styles.passwordWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={errors.includes('password') ? styles.inputError : ''}
                    value={formData.password}
                    onChange={(e) => {
                      const noSpaces = e.target.value.replace(/\s/g, '');
                      setFormData({ ...formData, password: noSpaces });
                    }}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShowPassword(v => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {authMode === 'register' && (
                <div className={styles.inputGroup}>
                  <label className={errors.includes('gender') ? styles.labelError : ''}>Płeć</label>
                  <div className={styles.genderGrid}>
                    <button
                      type="button"
                      className={`${styles.genderBtn} ${formData.gender === 'female' ? styles.genderBtnActive : ''}`}
                      onClick={() => setFormData({ ...formData, gender: 'female' })}
                    >
                      Kobieta {formData.gender === 'female' && ' ✓'}
                    </button>
                    <button
                      type="button"
                      className={`${styles.genderBtn} ${formData.gender === 'male' ? styles.genderBtnActive : ''}`}
                      onClick={() => setFormData({ ...formData, gender: 'male' })}
                    >
                      Mężczyzna {formData.gender === 'male' && ' ✓'}
                    </button>
                  </div>
                </div>
              )}

              {authMode === 'login' && (
                <div className={styles.loginExtras}>
                  <label className={styles.rememberRow}>
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    Nie wylogowywuj mnie
                  </label>
                  <button
                    type="button"
                    className={styles.forgotPasswordBtn}
                    onClick={handleForgotPassword}
                    disabled={isSendingReset}
                  >
                    {isSendingReset ? 'Wysyłanie...' : 'Zapomniałem hasła?'}
                  </button>
                </div>
              )}

              <button type="submit" className={styles.submitAuthBtn}>
                {authMode === 'login' ? 'ZALOGUJ SIĘ' : 'ZAREJESTRUJ SIĘ'}
              </button>
            </form>
          </div>
        </div>
      )}

      {(isMenuOpen || isAuthOpen) && <div className={styles.menuOverlay} onClick={() => { setIsMenuOpen(false); setIsAuthOpen(false); }} />}
    </>
  );
}

export default Header;